import {
  Home as HomeIcon,
  Order as OrdersIcon,
  Report as ReportIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Received as ReceivedIcon,
  OrderBilling as OrderBillinIcon,
  Tracking as TrackingIcon,
  Contacts as ContactsIcon,
} from '~components/Icons';
import { PATHS } from '~constants/index';
import { HELP_CENTER_URL } from '~constants/commons';
import {
  Roles,
  PartnerCompanyTypes,
  PlanFeaturesTypes,
} from '~globals/types/enums';
import { isOnlyCreatorOrder } from '~utils/user';
import { getUrlTrackingCompany } from '~utils/commons';
import { getFeatureAvailableToDate } from '~utils/feature';

import { LateralMenuItem } from './types';
import AvailablesRoutesStateWrapper from './AvailablesRoutesStateWrapper';
import RoutesIconWrapper from './RoutesIconWrapper';

export const MAIN_ITEMS: LateralMenuItem[] = [
  {
    data: {
      title: 'Escritorio',
      path: PATHS.HOME.BASE,
      icon: HomeIcon,
    },
    tooltipText:
      'Visualiza el día a día de tu empresa, con estadísticas en tiempo real.',
    permissions: {
      roles: {
        UI_V1: [Roles.Admin, Roles.Operator, Roles.Reader],
        UI_V2: [Roles.Admin, Roles.Operator, Roles.Reader, Roles.CreatorOrder],
      },
      companyType: [PartnerCompanyTypes.Carrier, PartnerCompanyTypes.Giver],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
  {
    data: {
      title: 'Colecta',
      path: PATHS.RECEIVED.BASE,
      icon: ReceivedIcon,
      chips: {
        activeVersion: '1.16.0',
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmGiversModule,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmGiversModule,
          ),
        },
      },
    },
    tooltipText:
      'Gestiona los pedidos que te envían tus vendedores o dadores de carga.',
    permissions: {
      roles: [Roles.Admin, Roles.Operator, Roles.Reader],
      companyType: [PartnerCompanyTypes.Carrier],
      validateCrossCompanyEnabled: true,
      showOnUIV2: false,
    },
  },
  {
    data: {
      title: 'Pedidos',
      path: PATHS.ORDERS.BASE,
      icon: OrdersIcon,
    },
    tooltipText: (currentUser) =>
      isOnlyCreatorOrder(currentUser)
        ? 'Aquí podrás gestionar tus pedidos y hacer seguimiento de visitas.'
        : 'Aquí podrás gestionar tus pedidos, generar rutas y hacer seguimiento de visitas.',
    permissions: {
      roles: [Roles.Admin, Roles.Operator, Roles.Reader, Roles.CreatorOrder],
      companyType: [PartnerCompanyTypes.Carrier, PartnerCompanyTypes.Giver],
      validateCrossCompanyEnabled: false,
      showOnUIV2: false,
    },
  },
  {
    data: {
      title: 'Rutas',
      path: PATHS.ROUTES.BASE,
      icon: RoutesIconWrapper,
    },
    tooltipText:
      // eslint-disable-next-line max-len
      'Haz el seguimiento de todas tus rutas, recorridos y unidades en tránsito desde un solo lugar.',
    permissions: {
      roles: [Roles.Admin, Roles.Operator, Roles.Reader],
      companyType: [PartnerCompanyTypes.Carrier],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
    subItem: AvailablesRoutesStateWrapper,
  },
];

export const SUB_ITEMS: LateralMenuItem[] = [
  {
    data: {
      title: 'Seguimiento de pedidos',
      path: (currentUser) =>
        getUrlTrackingCompany(currentUser?.companyId ?? ''),
      target: '_blank',
      icon: TrackingIcon,
      chips: {
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.TrackingPortalModule,
        },
      },
    },
    tooltipText:
      'Visualiza la página de seguimiento de pedidos para clientes (Abre una nueva ventana).',
    permissions: {
      roles: [Roles.Admin],
      companyType: [PartnerCompanyTypes.Carrier],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
  {
    data: {
      title: 'Contactos Frecuentes',
      path: PATHS.CUSTOMERS.BASE,
      icon: ContactsIcon,
    },
    tooltipText:
      // eslint-disable-next-line max-len
      'Gestiona de manera rápida los contactos que realizas visitas de manera frecuente.',
    permissions: {
      roles: [Roles.Admin, Roles.Operator, Roles.Reader],
      companyType: [PartnerCompanyTypes.Carrier],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
];

export const ADMIN_ITEMS: LateralMenuItem[] = [
  {
    data: {
      title: 'Reportes',
      path: PATHS.REPORTS.BASE,
      icon: ReportIcon,
    },
    tooltipText: 'Obtén métricas personalizadas del desempeño de tu empresa.',
    permissions: {
      roles: [Roles.Admin],
      companyType: [PartnerCompanyTypes.Carrier, PartnerCompanyTypes.Giver],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
  {
    data: {
      title: 'Liquidación de pedidos',
      path: PATHS.ORDER_BILLING.BASE,
      icon: OrderBillinIcon,
      chips: {
        activeVersion: '1.19.6',
      },
    },
    tooltipText:
      'Gestiona la liquidación de tus pedidos y los de tus dadores de carga / vendedores.',
    permissions: {
      roles: [Roles.Admin],
      companyType: [PartnerCompanyTypes.Carrier],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
  {
    data: {
      title: 'Configuración',
      path: PATHS.CONFIG.BASE,
      icon: SettingsIcon,
    },
    tooltipText:
      'Gestiona preferencias de uso, entidades y personaliza la plataforma a tu medida.',
    permissions: {
      roles: [Roles.Admin],
      companyType: [PartnerCompanyTypes.Carrier, PartnerCompanyTypes.Giver],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
];

export const HELPER_ITEMS: LateralMenuItem[] = [
  {
    data: {
      title: 'Ayuda',
      path: HELP_CENTER_URL,
      target: '_blank',
      icon: HelpIcon,
    },
    tooltipText:
      'Recorre el centro de ayuda online para obtener más información de la herramienta.',
    permissions: {
      roles: [Roles.Admin, Roles.Operator, Roles.Reader, Roles.CreatorOrder],
      companyType: [PartnerCompanyTypes.Carrier, PartnerCompanyTypes.Giver],
      validateCrossCompanyEnabled: false,
      showOnUIV2: true,
    },
  },
];
